import { requestShiftAuditCommentPrompt } from "@/lib/shiftAuditCommentPromptStore"

type ShiftAuditAction = "update" | "cancel"

type BodyWithAuditComment = {
  auditComment?: string | null
}

type ShiftAuditCommentOptions = {
  skipPrompt?: boolean
}

const SHIFT_AUDIT_COMMENT_PROMPTS = {
  update: {
    title: "Add Update Comment",
    description: "Add a short note describing why this block is being updated.",
    placeholder: "Explain the change",
    missingCommentError: "Update comment is required to save block changes.",
  },
  cancel: {
    title: "Add Cancellation Comment",
    description: "Add a short note describing why this block is being cancelled.",
    placeholder: "Explain the cancellation",
    missingCommentError: "Cancellation comment is required to cancel this block.",
  },
} as const

const normalizeAuditComment = (auditComment?: string | null) => {
  const normalizedAuditComment = typeof auditComment === "string" ? auditComment.trim() : ""

  return normalizedAuditComment || undefined
}

export const resolveShiftAuditComment = async ({
  action,
  auditComment,
  skipPrompt,
}: {
  action: ShiftAuditAction
  auditComment?: string | null
  skipPrompt?: boolean
}): Promise<string | undefined> => {
  const normalizedAuditComment = normalizeAuditComment(auditComment)

  if (normalizedAuditComment) {
    return normalizedAuditComment
  }

  if (skipPrompt) {
    return undefined
  }

  const promptedComment = await requestShiftAuditCommentPrompt(SHIFT_AUDIT_COMMENT_PROMPTS[action])

  return normalizeAuditComment(promptedComment)
}

export const addShiftAuditComment = async <T extends BodyWithAuditComment>({
  body,
  action,
  options,
}: {
  body: T
  action: ShiftAuditAction
  options?: ShiftAuditCommentOptions
}): Promise<T> => {
  const auditComment = await resolveShiftAuditComment({
    action,
    auditComment: body.auditComment,
    skipPrompt: options?.skipPrompt ?? true,
  })

  if (auditComment === undefined) {
    return body
  }

  return { ...body, auditComment }
}

export function withUpdateAuditComment<T extends BodyWithAuditComment>(
  body: T,
  options?: ShiftAuditCommentOptions,
): Promise<T> {
  return addShiftAuditComment<T>({ body, action: "update", options })
}

export function withCancellationAuditComment<T extends BodyWithAuditComment>(
  body: T,
  options?: ShiftAuditCommentOptions,
): Promise<T> {
  return addShiftAuditComment<T>({ body, action: "cancel", options })
}
